import { findAllIterator, OptionsWithRql, rqlBuilder, UserData } from '@extrahorizon/javascript-sdk';
import { useQuery } from '@tanstack/react-query';
import { Note } from 'models/general';
import { sdk, queryKeys, SCHEMA_NAMES, assertAffectedRecord } from 'utils/query';
import { EmptyParametersError } from './errors/EmptyParametersError';

export const useComments = (measurementId?: string) =>
  useQuery({
    queryKey: [queryKeys.measurementComments, measurementId],
    queryFn: () => getComments(measurementId),
    enabled: !!measurementId,
  });

export async function getComments(measurementId?: string) {
  if (!measurementId) throw new EmptyParametersError();
  const rql = rqlBuilder().sort('-creationTimestamp').build();
  const find = async (options: OptionsWithRql) =>
    await sdk.data.comments.find(SCHEMA_NAMES.heartRhythm, measurementId, options);
  const iterator = findAllIterator<Note>(find, { rql });

  const notes: Note[] = [];

  for await (const comments of iterator) {
    if (comments.data.length > 0) {
      const users: string[] = [];
      comments.data.forEach((comment) => {
        if (comment.creatorId && users.indexOf(comment.creatorId) < 0) {
          users.push(comment.creatorId);
        }
      });

      const userList = await getUserNames(users);

      const newNotes = comments.data.map((comment) => {
        const creator = userList.find((user) => user.id === comment.creatorId);
        const name = creator ? `${creator.lastName} ${creator.firstName}` : '???';
        return { ...comment, name };
      });
      notes.push(...newNotes);
    }
  }

  return notes;
}

export const addComment = async (variables: { measurementId?: string; text: string }) => {
  const { measurementId, text } = variables;
  if (!measurementId) throw new EmptyParametersError();
  await sdk.data.comments.create(SCHEMA_NAMES.heartRhythm, measurementId, { text });
};

export const deleteComment = async (variables: { measurementId?: string; commentId: string }) => {
  const { measurementId, commentId } = variables;
  if (!measurementId || !commentId) throw new EmptyParametersError();
  const result = await sdk.data.comments.remove(commentId, SCHEMA_NAMES.heartRhythm, measurementId);
  return assertAffectedRecord({ result });
};

export const updateComment = async (variables: { measurementId?: string; commentId: string; text: string }) => {
  const { measurementId, commentId, text } = variables;
  if (!measurementId || !commentId) throw new EmptyParametersError();
  const result = await sdk.data.comments.update(commentId, SCHEMA_NAMES.heartRhythm, measurementId, { text });
  return assertAffectedRecord({ result });
};

export const useUserNameById = (ids: string[]) =>
  useQuery({ queryKey: [queryKeys.userName, ids], queryFn: () => getUserNames(ids) });

export async function getUserNames(ids: string[]) {
  const rql = rqlBuilder().in('id', ids).select(['id', 'first_name', 'last_name']).build();

  const find = async (options: OptionsWithRql) => await sdk.users.find(options);
  const iterator = findAllIterator(find, { rql });

  const users: UserData[] = [];

  for await (const user of iterator) {
    users.push(...(user.data as UserData[]));
  }

  return users;
}
